// Essai guidé du parcours « Configurer mon piano » : quelques notes isolées, puis une note
// répétée, évalués à partir des événements d'entrée. Logique pure, sans navigateur, pour être
// testée ; l'écran ne fait qu'afficher l'état, les consignes et le verdict.

import type { Confidence, InputEvent } from '../engine/types';

/** Notes différentes à jouer une par une. */
export const ISOLATED_TARGET = 3;
/** Frappes consécutives de la même note. */
export const REPEATED_TARGET = 4;
/** Part maximale de détections incertaines pour conclure que la reconnaissance est fiable. */
export const UNCERTAIN_RATIO_MAX = 0.3;

export type TrialPhase = 'isolated' | 'repeated' | 'done';

export interface TrialState {
  phase: TrialPhase;
  /** Notes distinctes reconnues avec certitude pendant l'étape « notes isolées ». */
  isolated: number[];
  /** Note répétée en cours et nombre de frappes consécutives reconnues. */
  repeated: { pitch: number | null; count: number };
  /** Détections sûres, incertaines, et attaques sans hauteur reconnue. */
  sure: number;
  uncertain: number;
  onsets: number;
  last: { pitch: number | null; confidence: Confidence | 'none'; time: number } | null;
}

export function createTrial(): TrialState {
  return { phase: 'isolated', isolated: [], repeated: { pitch: null, count: 0 }, sure: 0, uncertain: 0, onsets: 0, last: null };
}

/** Applique un événement d'entrée et renvoie le nouvel état (l'ancien n'est pas modifié). */
export function applyEvent(state: TrialState, event: InputEvent): TrialState {
  if (state.phase === 'done') return state;
  if (event.type === 'onset') return { ...state, onsets: state.onsets + 1, last: { pitch: null, confidence: 'none', time: event.time } };
  if (event.type !== 'noteOn') return state;
  const last = { pitch: event.pitch, confidence: event.confidence, time: event.time };
  // Une détection incertaine est montrée mais ne fait jamais avancer l'essai : même règle
  // que pendant un exercice, où elle n'est jamais comptée comme juste.
  if (event.confidence !== 'sure') return { ...state, uncertain: state.uncertain + 1, last };
  const next: TrialState = { ...state, sure: state.sure + 1, last };
  if (state.phase === 'isolated') {
    const isolated = state.isolated.includes(event.pitch) ? state.isolated : [...state.isolated, event.pitch];
    next.isolated = isolated;
    if (isolated.length >= ISOLATED_TARGET) next.phase = 'repeated';
    return next;
  }
  const count = state.repeated.pitch === event.pitch ? state.repeated.count + 1 : 1;
  next.repeated = { pitch: event.pitch, count };
  if (count >= REPEATED_TARGET) next.phase = 'done';
  return next;
}

export interface TrialProgress {
  phase: TrialPhase;
  done: number;
  target: number;
}

export function progressOf(state: TrialState): TrialProgress {
  if (state.phase === 'isolated') return { phase: 'isolated', done: state.isolated.length, target: ISOLATED_TARGET };
  if (state.phase === 'repeated') return { phase: 'repeated', done: state.repeated.count, target: REPEATED_TARGET };
  return { phase: 'done', done: REPEATED_TARGET, target: REPEATED_TARGET };
}

export type TrialVerdict = 'ok' | 'uncertain' | 'incomplete';

export interface TrialReport {
  verdict: TrialVerdict;
  sure: number;
  uncertain: number;
  onsets: number;
  /** Part des détections incertaines parmi toutes les notes détectées (0 à 1). */
  uncertainRatio: number;
  /** Raisons d'un verdict « incertain », en français, pour l'écran. */
  reasons: string[];
}

export function reportOf(state: TrialState): TrialReport {
  const detections = state.sure + state.uncertain;
  const uncertainRatio = detections === 0 ? 0 : state.uncertain / detections;
  const reasons: string[] = [];
  if (uncertainRatio > UNCERTAIN_RATIO_MAX) reasons.push(`${Math.round(uncertainRatio * 100)} % des notes ont été reconnues avec doute (${state.uncertain} sur ${detections}).`);
  // Une attaque sans hauteur pour deux notes sûres, ou plus : la reconnaissance rate trop de notes.
  if (state.onsets > 0 && state.onsets * 2 >= state.sure) reasons.push(`${state.onsets} attaque${state.onsets > 1 ? 's' : ''} entendue${state.onsets > 1 ? 's' : ''} sans hauteur reconnue, pour ${state.sure} note${state.sure > 1 ? 's' : ''} sûre${state.sure > 1 ? 's' : ''}.`);
  const verdict: TrialVerdict = state.phase !== 'done' ? 'incomplete' : reasons.length > 0 ? 'uncertain' : 'ok';
  return { verdict, sure: state.sure, uncertain: state.uncertain, onsets: state.onsets, uncertainRatio, reasons };
}
